import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plugs,
  ChatCircleDots,
  Rocket,
  ArrowRight,
  Check,
  CircleNotch,
} from "@phosphor-icons/react";
import axios from "axios";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Målsystem vi lyfter fram publikt — samma lista som visas som radio-knappar
// i formuläret så det finns en visuell röd tråd genom sidan.
const TARGETS = [
  { key: "fortnox", label: "Fortnox", tag: "Bokföring" },
  { key: "visma", label: "Visma", tag: "Bokföring" },
  { key: "bokio", label: "Bokio", tag: "Bokföring" },
  { key: "slack", label: "Slack", tag: "Kommunikation" },
  { key: "teams", label: "Microsoft Teams", tag: "Kommunikation" },
  { key: "annat", label: "Eget affärssystem", tag: "Custom" },
];

const STEPS = [
  {
    icon: ChatCircleDots,
    title: "1. Berätta vad ni vill",
    body:
      "Fyll i formuläret så bokar vi ett kort möte. Vi lyssnar på ert flöde och skickar en fast offert inom 3 arbetsdagar.",
  },
  {
    icon: Plugs,
    title: "2. Vi bygger kopplingen",
    body:
      "Ni får en dedikerad API-nyckel per system. Vi bygger en adapter mot ert affärssystem — läsning, skrivning eller båda hållen.",
  },
  {
    icon: Rocket,
    title: "3. Ni går live",
    body:
      "Färdig integration med dokumentation och test-checklista. Löpande support ingår första 30 dagarna, sedan valfritt SLA.",
  },
];

const PRICE_TIERS = [
  {
    label: "Enkel export",
    from: "från 5 000 kr",
    body:
      "En riktning: vi läser tidrapporter/objekt ur Tidsrapporten och skickar till ert system (t.ex. månadsvis Fortnox-verifikat).",
  },
  {
    label: "Tvåvägs-sync",
    from: "från 15 000 kr",
    body:
      "Ni skapar projekt/kunder i ert CRM — de dyker upp i Tidsrapporten. Tider stämplas där, faktureringsunderlag pushas tillbaka.",
    popular: true,
  },
  {
    label: "Realtid + webhooks",
    from: "från 25 000 kr",
    body:
      "Push-notiser till Slack/Teams när någon stämplar ut, budgeter överskrids eller när en rapport är klar för attestering.",
  },
];

export default function PublicIntegrations() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    integration_type: "fortnox",
    team_size: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/leads/integration-inquiry`, form);
      setSubmitted(true);
      toast.success("Tack! Vi hör av oss inom 24 timmar.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Något gick fel — försök igen eller maila oss direkt.";
      toast.error(typeof detail === "string" ? detail : "Fel vid skickning");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="public-integrations-page"
      className="min-h-screen bg-app text-text-primary"
    >
      {/* NAV — identisk struktur som Landing.jsx för visuell kontinuitet */}
      <header
        className={
          "sticky top-0 z-40 transition-all " +
          (scrolled ? "bg-app/90 backdrop-blur border-b border-app" : "bg-transparent")
        }
      >
        <nav className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="integrations-nav-brand"
          >
            <span className="h-8 w-8 rounded-md bg-sidebar flex items-center justify-center text-sidebar-text font-heading text-sm font-bold">
              T
            </span>
            <span className="font-heading text-lg tracking-tight">
              Tidsrapporten
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm">
            <Link to="/" className="hover:text-brand transition-colors">
              Hem
            </Link>
            <Link to="/pricing" className="hover:text-brand transition-colors">
              Priser
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm hover:text-brand transition-colors"
            >
              Logga in
            </Link>
            <button
              onClick={() => navigate("/register")}
              className="text-sm bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md transition-colors"
              data-testid="integrations-nav-cta"
            >
              Prova gratis
            </button>
          </div>
        </nav>
      </header>

      {/* HERO */}
      <section>
        <div className="mx-auto max-w-4xl px-6 pt-20 md:pt-28 pb-16 text-center">
          <span
            className="inline-block text-[11px] tracking-widest uppercase text-text-muted mb-6"
            data-testid="integrations-hero-eyebrow"
          >
            Integrationer på beställning
          </span>
          <h1
            className="font-heading text-5xl md:text-6xl tracking-tight leading-[1.03]"
            data-testid="integrations-hero-title"
          >
            Vi bygger kopplingen
            <br />
            <span className="text-brand">mot ert system.</span>
          </h1>
          <p className="mt-6 text-lg text-text-muted max-w-2xl mx-auto leading-relaxed">
            Tidsrapporten har ett öppet API. Vi bygger färdiga adaptrar mot
            Fortnox, Visma, Bokio, Slack, Teams och era egna affärssystem —
            fast pris, dokumentation ingår, aktiverat inom 2–4 veckor.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <a
              href="#formular"
              className="bg-brand hover:bg-brand-hover text-white px-6 py-3.5 rounded-md font-medium inline-flex items-center gap-2 transition-colors"
              data-testid="integrations-hero-cta"
            >
              Beskriv ert behov
              <ArrowRight size={18} weight="bold" />
            </a>
            <Link
              to="/pricing"
              className="border border-app hover:bg-surface-hover px-6 py-3.5 rounded-md font-medium transition-colors"
            >
              Se abonnemangspriser
            </Link>
          </div>
        </div>
      </section>

      {/* TARGET SYSTEMS */}
      <section
        className="border-t border-app bg-surface"
        data-testid="integrations-targets"
      >
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="text-center mb-12">
            <span className="text-[11px] tracking-widest uppercase text-text-muted">
              Populära kopplingar
            </span>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl tracking-tight">
              Ni väljer system — vi bygger bryggan
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {TARGETS.map((t) => (
              <div
                key={t.key}
                className="bg-app border border-app rounded-xl p-6 hover:border-brand/40 transition-colors"
                data-testid={`integrations-target-${t.key}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-heading text-lg">{t.label}</span>
                  <span className="text-[10px] tracking-widest uppercase text-text-muted">
                    {t.tag}
                  </span>
                </div>
                <p className="mt-2 text-xs text-text-muted">
                  {t.key === "annat"
                    ? "Egen SOAP, SFTP eller REST — vi bygger adaptrar mot äldre eller ovanliga system."
                    : "Läsning, skrivning eller båda hållen. Vi mappar era konton och projekt."}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-text-muted">
            Ser inte ert system? <a href="#formular" className="text-brand hover:underline">Berätta om det →</a>
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="border-t border-app"
        data-testid="integrations-steps"
      >
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="max-w-2xl mb-14">
            <span className="text-[11px] tracking-widest uppercase text-text-muted">
              Så här går det till
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight leading-tight">
              Tre steg från idé till driftsatt integration.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="bg-surface border border-app rounded-xl p-7"
                data-testid={`integrations-step-${i}`}
              >
                <div className="h-11 w-11 rounded-lg bg-brand/10 flex items-center justify-center text-brand mb-5">
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className="font-heading text-xl mb-3">{title}</h3>
                <p className="text-sm text-text-muted leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICE TIERS */}
      <section
        className="bg-sidebar text-sidebar-text"
        data-testid="integrations-prices"
      >
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center mb-14">
            <span className="text-[11px] tracking-widest uppercase text-sidebar-text/70">
              Riktpriser
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight">
              Fast pris — inga överraskningar.
            </h2>
            <p className="mt-5 text-sidebar-text/80 max-w-xl mx-auto">
              Alla nivåer inkluderar dokumentation, test-checklista och 30
              dagars support. Löpande support därefter enligt separat SLA.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {PRICE_TIERS.map((t) => (
              <div
                key={t.label}
                className={
                  "relative rounded-2xl p-8 border transition-all " +
                  (t.popular
                    ? "bg-app text-text-primary border-brand shadow-xl md:-translate-y-2"
                    : "bg-sidebar-active border-sidebar-active hover:border-brand/50")
                }
                data-testid={`integrations-tier-${t.label}`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand text-white text-[11px] tracking-widest uppercase px-3 py-1 rounded-full">
                    Vanligast
                  </span>
                )}
                <div
                  className={
                    "text-[11px] tracking-widest uppercase " +
                    (t.popular ? "text-text-muted" : "text-sidebar-text/70")
                  }
                >
                  {t.label}
                </div>
                <div className="mt-4 font-heading text-3xl">{t.from}</div>
                <p
                  className={
                    "mt-4 text-sm leading-relaxed " +
                    (t.popular ? "text-text-muted" : "text-sidebar-text/80")
                  }
                >
                  {t.body}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-xs text-sidebar-text/70">
            Priser är exempel för typiska scope. Ni får en fast offert efter
            behovsanalys.
          </p>
        </div>
      </section>

      {/* FORM */}
      <section
        id="formular"
        className="border-t border-app"
        data-testid="integrations-form-section"
      >
        <div className="mx-auto max-w-3xl px-6 py-20">
          <div className="text-center mb-10">
            <span className="text-[11px] tracking-widest uppercase text-text-muted">
              Beskriv ert behov
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight leading-tight">
              Vi återkommer inom 24 timmar.
            </h2>
          </div>

          {submitted ? (
            <div
              className="bg-surface border border-brand/40 rounded-2xl p-10 text-center"
              data-testid="integrations-form-success"
            >
              <div className="h-14 w-14 rounded-full bg-brand/10 text-brand flex items-center justify-center mx-auto mb-5">
                <Check size={28} weight="bold" />
              </div>
              <h3 className="font-heading text-2xl mb-3">
                Tack — vi hörde dig!
              </h3>
              <p className="text-text-muted max-w-md mx-auto">
                Vi återkommer inom 24 timmar på {form.email}. Under tiden kan
                du gärna testa själva appen gratis.
              </p>
              <button
                onClick={() => navigate("/register")}
                className="mt-6 bg-brand hover:bg-brand-hover text-white px-6 py-3 rounded-md font-medium inline-flex items-center gap-2 transition-colors"
                data-testid="integrations-form-success-cta"
              >
                Starta gratis prövotid
                <ArrowRight size={16} weight="bold" />
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-surface border border-app rounded-2xl p-8 md:p-10 space-y-5"
              data-testid="integrations-form"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-sm font-medium">Ditt namn *</span>
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="mt-1.5 w-full border border-app rounded-md px-3 py-2.5 bg-app focus:outline-none focus:border-brand"
                    data-testid="integrations-form-name"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium">Företag *</span>
                  <input
                    required
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    className="mt-1.5 w-full border border-app rounded-md px-3 py-2.5 bg-app focus:outline-none focus:border-brand"
                    data-testid="integrations-form-company"
                  />
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-sm font-medium">E-post *</span>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="mt-1.5 w-full border border-app rounded-md px-3 py-2.5 bg-app focus:outline-none focus:border-brand"
                    data-testid="integrations-form-email"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium">Telefon</span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Frivilligt"
                    className="mt-1.5 w-full border border-app rounded-md px-3 py-2.5 bg-app focus:outline-none focus:border-brand"
                    data-testid="integrations-form-phone"
                  />
                </label>
              </div>

              <div>
                <span className="text-sm font-medium block mb-2">
                  Vilket system vill ni koppla mot?
                </span>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {TARGETS.map((t) => (
                    <label
                      key={t.key}
                      className={
                        "cursor-pointer border rounded-md px-3 py-2 text-sm text-center transition-colors " +
                        (form.integration_type === t.key
                          ? "border-brand bg-brand/10 text-brand"
                          : "border-app hover:border-brand/50")
                      }
                    >
                      <input
                        type="radio"
                        name="integration_type"
                        value={t.key}
                        checked={form.integration_type === t.key}
                        onChange={update("integration_type")}
                        className="sr-only"
                        data-testid={`integrations-form-type-${t.key}`}
                      />
                      {t.label}
                    </label>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="text-sm font-medium">Antal medarbetare</span>
                <input
                  type="text"
                  value={form.team_size}
                  onChange={update("team_size")}
                  placeholder="Frivilligt — t.ex. 8"
                  className="mt-1.5 w-full border border-app rounded-md px-3 py-2.5 bg-app focus:outline-none focus:border-brand"
                  data-testid="integrations-form-team-size"
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium">
                  Beskriv vad ni vill uppnå *
                </span>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Vi vill att tidrapporter automatiskt skickas till Fortnox varje månad, plus att våra projekt från Pipedrive dyker upp som objekt i Tidsrapporten…"
                  className="mt-1.5 w-full border border-app rounded-md px-3 py-2.5 bg-app focus:outline-none focus:border-brand resize-y"
                  data-testid="integrations-form-message"
                />
                <span className="text-xs text-text-muted mt-1 block">
                  Minst 10 tecken. Ju mer detaljer desto snabbare offert.
                </span>
              </label>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-brand hover:bg-brand-hover text-white px-6 py-3 rounded-md font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="integrations-form-submit"
                >
                  {submitting ? (
                    <>
                      <CircleNotch size={16} weight="bold" className="animate-spin" />
                      Skickar…
                    </>
                  ) : (
                    <>
                      Skicka förfrågan
                      <ArrowRight size={16} weight="bold" />
                    </>
                  )}
                </button>
                <p className="text-xs text-text-muted">
                  Vi delar aldrig era uppgifter med tredje part.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-sidebar text-sidebar-text/70" data-testid="integrations-footer">
        <div className="mx-auto max-w-6xl px-6 py-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="flex items-center gap-2 text-sidebar-text">
              <span className="h-7 w-7 rounded bg-brand flex items-center justify-center font-heading text-xs font-bold text-white">
                T
              </span>
              <span className="font-heading text-base">Tidsrapporten</span>
            </div>
            <p className="mt-3 text-xs">
              Tidrapportering, objekthantering och filarkiv för team som
              fakturerar tid mot projekt.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <Link to="/" className="hover:text-white transition-colors">
              Hem
            </Link>
            <Link to="/pricing" className="hover:text-white transition-colors">
              Priser
            </Link>
            <Link to="/login" className="hover:text-white transition-colors">
              Logga in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
